<script setup lang="ts">
import useVuelidate from '@vuelidate/core'

import { Contract, CustomerTypeEnum, Form } from '~/layers/forms/models'
import type { CoverageComparatorListExpose } from '~/layers/forms/types/coverageComparatorDefinedExpose'

definePageMeta({
  middleware: 'forms-configurations',
})

const developmentTools = ref<boolean>(false)
const { Debug } = stores()

const { formConfig, activeStep, continueToStep, isLastStep } = useFormBuilder()

const confirm = useConfirm()
const toast = useToast()
const focus = useFocus()
const { constraints } = useContractConstraints()
const model = ref<Contract>(Contract.load())
const containerRef = ref<HTMLElement | null>(null)

const { RemoteConfigurationStore, LocalConfigurationStore } = stores()

const mainContainerRef = ref<HTMLElement | null>(null)

const { cloned: submitResult, sync: resetSubmitResult } = useCloned<SubmitResult>({})

const insurable = useInsurable({
  product: computed(() => RemoteConfigurationStore.value?.ramoEnum),
  contract: model,
})

const v$ = useVuelidate()

const { submit: sendLead } = useLead({
  contract: model,
  insurable: insurable,
})

const currentStepIsValid = () => {
  const formsToValidate = formConfig.value[activeStep.value]?.forms
  formsToValidate?.forEach(async (form) => {
    await v$.value?.[form]?.$validate()
  })

  const invalidForm = formsToValidate?.find((form) => v$.value?.[form]?.$invalid)

  if (invalidForm) {
    const element = document.getElementById(`${invalidForm}-form`)
    if (element) focus({ target: element })
    return false
  }
  return true
}

const selectedCompany = computed(() =>
  LocalConfigurationStore.value?.associatedCompanies?.find(
    (c) => c.argentoInsuranceId === model.value.coverage.argentoInsuranceCompanyId
  )
)

const heading = computed(() => {
  if (currentStepHasCoverageForm.value && RemoteConfigurationStore.value?.initBy === 'BySubRamo')
    return { main: `Comparador de ${RemoteConfigurationStore.value?.ramoEnum}` }
  if (!currentStepHasCoverageForm.value && RemoteConfigurationStore.value?.initBy === 'BySubRamo')
    return {
      main: `Seguro de ${RemoteConfigurationStore.value?.ramoEnum}`,
      sub: LocalConfigurationStore.value?.associatedCompanies?.find(
        (c) => c.argentoInsuranceId === model.value.coverage.argentoInsuranceCompanyId
      )?.name,
    }
  return {
    main: `${RemoteConfigurationStore.value?.producto?.alias}`,
    sub: `${RemoteConfigurationStore.value?.emisor?.nombre}`,
  }
})

const currentStepHasCoverageForm = computed(() =>
  formConfig.value[activeStep.value]?.forms.some((form) => form === Form.coverage)
)

const process = computed(() => {
  const message = 'Cargando...'
  //if (submitionStatus.value === 'pending') message = 'Procesando solicitud, espere unos minutos...'

  return {
    loading: submitionStatus.value === 'pending',
    message,
  }
})

const coverageListComponentInstance = templateRef<CoverageListExpose[]>('coverage-list')

const coverageQuotationsStatus = computed(() =>
  coverageListComponentInstance.value?.[0]?.getQuotationsStatus()
)

const coverageComparatorComponentInstance =
  templateRef<CoverageComparatorListExpose[]>('coverage-comparator')

const coverageComparatorQuotationsStatus = computed(() =>
  coverageComparatorComponentInstance.value?.[0]?.getQuotationsStatus()
)

const fetchQuotations = (selectedOnly: boolean = false) => {
  coverageComparatorComponentInstance.value?.[0]?.fetchQuotations({ selectedOnly })
}

const {
  execute: submitData,
  data: submitionResponse,
  status: submitionStatus,
} = useSiteApi('/api/Contratos/Create', {
  method: 'POST',
  body: computed(() => Contract.map.toProductoContratoMasterRequestModel(model.value)),
  watch: false,
  immediate: false,
})

const coverageStatusMesages: Partial<Record<Exclude<CoveragePlanStatus, 'quoted'>, string>> = {
  error: 'Hubo un error al recalcular el monto de la cobertura del seguro. Por favor reintentar.',
  pending:
    'Estamos recalculado el monto de la cobertura del seguro. Por favor espera antes de continuar.',
}

const nextStep = async () => {
  if (
    !!coverageComparatorQuotationsStatus.value?.selectedStatus &&
    coverageComparatorQuotationsStatus.value.selectedStatus !== 'success'
  ) {
    toast.add({
      severity: 'secondary',
      summary: 'Cobertura',
      detail: coverageStatusMesages[coverageComparatorQuotationsStatus.value.selectedStatus],
      life: 5000,
    })
    return
  }

  if (!currentStepIsValid()) return

  continueToStep()
  if (containerRef.value) focus({ target: containerRef.value })
  submitLead()
}

const previusStep = () => {
  continueToStep('previous')
  if (mainContainerRef.value) focus({ target: mainContainerRef.value })
}

const submitLead = (
  { processFinalized }: { processFinalized?: boolean } = { processFinalized: false }
) => {
  if (processFinalized) {
    sendLead('Completado')
    return
  }

  const currentStage = computed<SiteApiSchema<'OportunidadEtapa'>>(() => {
    const previousStep = activeStep.value - 1
    const stage =
      formConfig.value[isLastStep.value ? activeStep.value : previousStep]?.argantoInsuranceStage ??
      'NoDefinido'

    return stage
  })

  sendLead(currentStage.value)
}

const submit = useDebounceFn(
  async () => {
    try {
      await submitData()
      if (submitionResponse.value?.codigoRespuesta === '00') {
        submitResult.value.result = 'SUCCESS'
        submitLead({ processFinalized: true })
      } else {
        submitResult.value.result = 'FAILED'
        submitResult.value.reason =
          submitionResponse.value?.codigoRespuesta === '99' ? 'FINANCE' : 'PROCESS_ERROR'
      }
    } catch {
      submitResult.value = {
        result: 'FAILED',
        reason: 'PROCESS_ERROR',
      }
    }
  },
  500,
  { maxWait: 60000 }
)

const submitConfirm = async () => {
  if (!currentStepIsValid()) return

  confirm.require({
    message: 'Está seguro de que desea continuar?',
    acceptProps: {
      label: 'Continuar',
    },
    rejectProps: {
      label: 'Cancelar',
    },
    accept: submit,
  })
}

onBeforeRouteLeave(async (to, from, next) => {
  if (submitResult.value.result === 'SUCCESS') return next()
  const confirmed = ref<boolean | null>(null)
  confirm.require({
    message: 'Está seguro que desea abandonar el proceso? Se perderá todo el progreso.',
    acceptProps: {
      label: 'Continuar',
    },
    rejectProps: {
      label: 'Cancelar',
    },
    accept: () => (confirmed.value = true),
    reject: () => (confirmed.value = false),
  })
  await until(confirmed).not.toBeNull()
  return next(confirmed.value ?? false)
})
</script>

<template>
  <div
    class="flex flex-col"
    :class="{
      'justify-center': submitResult.result || process.loading,
    }"
  >
    <!-- Development -->
    <template v-if="Debug">
      <div class="card flex justify-center">
        <PvDrawer
          v-model:visible="developmentTools"
          header="Data"
        >
          <div class="flex flex-col gap-4">
            <DevelopmentStoreViewer />
            <FormsDevelopmentDataViewer :data="model" />
          </div>
        </PvDrawer>
      </div>
      <div class="absolute top-0 right-0 z-[1000]">
        <PvButton
          class="sticky top-0"
          severity="contrast"
          @click="developmentTools = true"
        >
          <span class="icon-[ph--code]" />
        </PvButton>
      </div>
    </template>
    <!-- Development -->

    <div
      v-if="heading"
      ref="containerRef"
      class="flex gap-2 items-center my-8"
    >
      <div
        class="bg-primary rounded-lg p-2 md:p-3 aspect-square text-white flex items-center justify-center text-xl title-icon-container"
      >
        <span :class="getProductIcon(RemoteConfigurationStore?.ramoEnum)" />
      </div>
      <div class="self-stretch inline">
        <PvDivider layout="vertical" />
      </div>
      <div class="flex font-bold uppercase flex-col lg:gap-1 text-2xl sm:text-4xl">
        <span class="font-bold title-text items-center leading-none">
          {{ heading.main }}
        </span>
        <span class="text-xl title-text leading-none">
          {{ heading.sub }}
        </span>
      </div>
    </div>

    <FormsGlobalBlocker
      :argento-insurance-branch="RemoteConfigurationStore?.ramoEnum ?? 'None'"
      :loading="process.loading"
      :loading-message="process.message"
      :submit-result="submitResult"
      :reset-submit-result-function="resetSubmitResult"
    />

    <ClientOnly>
      <StepperComponent
        v-show="!submitResult.result && !process.loading"
        v-model="activeStep"
        :steps="formConfig"
        submit-label="Finalizar"
        emited-navigation
        :finalize-acction="false"
        @next-step="nextStep"
        @previous-step="previusStep"
        @end="submitConfirm"
      >
        <template
          v-for="(_, key) in formConfig"
          :key="key"
          #[`step-${key}`]="{ data }"
        >
          <div class="grid gap-4">
            <template
              v-for="form in data.forms"
              :key="form"
            >
              <template v-if="form === Form.coverage">
                <FormsCoverageComparatorList
                  v-if="RemoteConfigurationStore?.initBy === 'BySubRamo'"
                  ref="coverage-comparator"
                  v-model="model"
                  :insurable
                  :is-user-chosing="currentStepHasCoverageForm"
                  @selected="nextStep()"
                />
                <FormsCoverageList
                  v-else
                  ref="coverage-list"
                  v-model="model"
                  :insurable
                  :is-user-chosing="currentStepHasCoverageForm"
                  @selected="nextStep()"
                />
              </template>

              <FormsCustomerForm
                v-if="form === Form.customer"
                v-model="model.customer"
                :customer-as-insured="model.insured.customerAsInsured"
                :insurable
              >
                <template #aditional-fields>
                  <FormsPepFields
                    v-if="RemoteConfigurationStore?.form?.requiereCumplimiento"
                    v-model="model.pep"
                    :validation-scope="Form.customer"
                  />
                </template>
              </FormsCustomerForm>

              <FormsContactForm
                v-if="form === Form.contact && RemoteConfigurationStore?.form?.capturaContactos"
                v-model="model.contact"
              />

              <FormsInsuredForm
                v-if="form === Form.insured"
                v-model="model.insured"
                :customer="model.customer"
                :insurable
              >
                <template #aditional-fields>
                  <FormsPepFields
                    v-if="RemoteConfigurationStore?.form?.requiereCumplimiento"
                    v-model="model.pep"
                    :validation-scope="Form.customer"
                  />
                </template>
              </FormsInsuredForm>

              <FormsVehicleForm
                v-if="form === Form.vehicle || form === Form.moto"
                v-model="model.vehicle"
                :pre-filled="RemoteConfigurationStore?.initBy === 'BySubRamo'"
                :for-moto="form === Form.moto"
                :use-version="selectedCompany?.quoteWithVersion"
                :argento-insurance-company-id="model.coverage.argentoInsuranceCompanyId"
                :argento-insurance-product-id="model.coverage.argentoInsuranceProductId"
                :argento-insurancebroker-product-id="
                  model.coverage.argentoInsuranceCoverageRequestData?.brokerProductoId
                "
              />

              <FormsTravelForm
                v-if="form === Form.travel"
                v-model="model.travel"
                :insured="model.insured"
              />

              <FormsPetForm
                v-if="form === Form.pet"
                v-model="model.pet"
              />

              <FormsAddressForm
                v-if="form === Form.address && RemoteConfigurationStore?.form?.capturaDireccion"
                v-model="model.address"
                :argento-insurance-company-id="model.coverage.argentoInsuranceCompanyId"
                :argento-insurance-product-id="model.coverage.argentoInsuranceProductId"
                :argento-insurancebroker-product-id="
                  model.coverage.argentoInsuranceCoverageRequestData?.brokerProductoId
                "
              />

              <FormsRealStateAddressFrom
                v-if="form === Form.realStateAddress"
                v-model="model.realState.address"
                :customer-address="model.address"
                :argento-insurance-company-id="model.coverage.argentoInsuranceCompanyId"
                :argento-insurance-product-id="model.coverage.argentoInsuranceProductId"
                :argento-insurancebroker-product-id="
                  model.coverage.argentoInsuranceCoverageRequestData?.brokerProductoId
                "
              />

              <FormsDeviceForm
                v-if="form === Form.device"
                v-model="model.device"
              />

              <FormsBiometricsForm
                v-if="form === Form.biometrics && constraints?.captureBiometrics"
                v-model="model.biometrics"
              />

              <FormsInsuredAmountForm
                v-if="form === Form.insuredAmount"
                v-model="model.insuredAmount"
              />

              <FormsSpecialConditionsForm
                v-if="form === Form.specialConditions && constraints?.captureSpecialConditions"
                v-model="model.specialConditions"
              />

              <FormsAlternateVehicleForm
                v-if="form === Form.alternateVehicle"
                v-model="model.alternativeVehicle"
              />

              <FormsRealStateForm
                v-if="form === Form.realState"
                v-model="model.realState"
              />

              <FormsIdentificationForm
                v-if="form === Form.identification"
                v-model="model.customer.identification"
                :entity-type="CustomerTypeEnum.Person.code"
              />

              <FormsBeneficiaryCrud
                v-if="form === Form.beneficiaries"
                v-model="model.beneficiaries"
              />

              <FormsResumeSumary
                v-if="form === Form.resume"
                v-model="model"
                :data="model"
                @end-process="submitConfirm()"
              />

              <FormsAditionalInfoForm
                v-if="form === Form.aditionalInfo"
                v-model="model.aditionalInfo"
              />

              <FormsTosForm
                v-if="
                  form === Form.tos && LocalConfigurationStore?.brokerContext?.termsAndConditions
                "
                v-model="model.tos"
              />
            </template>
            <template v-if="isLastStep">
              <PvButton
                severity="success"
                class="col-span-full"
                :style="{
                  backgroundColor: LocalConfigurationStore?.brokerContext?.preset?.accentColorHex,
                }"
                @click="submitConfirm()"
              >
                <span class="flex items-center gap-2 py-2 text-xl font-bold uppercase md:text-2xl">
                  <span class="icon-[ph--shield-check-bold]" />
                  Finalizar
                </span>
              </PvButton>
            </template>
            <template
              v-if="
                ![Form.coverage, Form.resume].some((form) => data.forms.includes(form)) &&
                model.coverage.argentoInsuranceCompanyId
              "
            >
              <PvMessage
                v-if="coverageComparatorQuotationsStatus?.selectedStatus === 'error'"
                variant="outlined"
                severity="error"
                class="bg-blur sticky bottom-5 flex w-full justify-center py-2"
              >
                <div class="grid gap-2">
                  <div class="text-center">
                    Hubo un error al recalcular el monto de la cobertura del seguro.
                  </div>
                  <PvButton
                    severity="danger"
                    class="w-full"
                    @click="fetchQuotations(true)"
                  >
                    <span class="icon-[ph--arrow-counter-clockwise-bold]" />
                    Reintentar
                  </PvButton>
                </div>
              </PvMessage>
              <FormsCoverageResume
                v-else
                :coverage="model.coverage"
                card-layout="horizontal"
                bg="light"
                class="sticky bottom-5"
                :content-layout="$viewport.isGreaterThan('sm') ? 'horizontal' : 'vertical'"
                :status="
                  coverageQuotationsStatus ?? coverageComparatorQuotationsStatus?.selectedStatus
                "
              />
            </template>
          </div>
        </template>
      </StepperComponent>
    </ClientOnly>
  </div>
</template>
